import React, { useState, useEffect, useRef } from 'react';
import { Phone, PhoneOff, User } from 'lucide-react';
import { cn } from '../../lib/utils';

interface IncomingCallModalProps {
  callerNumber: string;
  candidateName?: string;
  candidateId?: string;
  callSid?: string;
  onAccept: () => void;
  onReject: () => void;
  autoRejectTimeout?: number;
}

export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  callerNumber,
  candidateName,
  candidateId,
  onAccept,
  onReject,
  autoRejectTimeout = 30000, // 30 seconds default
}) => {
  const isOpen = true; // Always open when rendered
  const [timeRemaining, setTimeRemaining] = useState(Math.floor(autoRejectTimeout / 1000));
  const audioRef = useRef<HTMLAudioElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isOpen) {
      console.log('[IncomingCallModal] Opening for call from:', callerNumber);
      
      // Play ringtone
      if (audioRef.current) {
        audioRef.current.loop = true;
        audioRef.current.play().catch((e) => {
          console.warn('[IncomingCallModal] Could not play ringtone:', e);
        });
      }

      // Start countdown
      setTimeRemaining(Math.floor(autoRejectTimeout / 1000));
      
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-reject timer
      timeoutRef.current = setTimeout(() => {
        console.log('[IncomingCallModal] Auto-rejecting after timeout');
        handleReject();
      }, autoRejectTimeout);

      return () => {
        // Cleanup
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isOpen, autoRejectTimeout]);

  const handleAccept = () => {
    console.log('[IncomingCallModal] Call accepted');
    
    // Stop ringtone
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Clear timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    onAccept();
  };

  const handleReject = () => {
    console.log('[IncomingCallModal] Call rejected');
    
    // Stop ringtone
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Clear timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    onReject();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Ringtone audio - using system beep as fallback */}
      <audio
        ref={audioRef}
        src="/sounds/ringtone.mp3"
        preload="auto"
        onError={() => {
          console.warn('[IncomingCallModal] Ringtone file not found');
        }}
      />

      {/* Fixed overlay modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-2xl border-2 border-primary/20 shadow-2xl p-6">
          <div className="text-center space-y-6 py-4">
            {/* Animated phone icon */}
            <div className="relative inline-flex">
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
              <div className="relative flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full">
                <Phone className="w-10 h-10 text-primary animate-pulse" />
              </div>
            </div>

            {/* Caller info */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">
                {candidateName || 'Incoming Call'}
              </h2>
              
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                {!candidateName && <User className="w-4 h-4" />}
                <p className="text-lg">{callerNumber}</p>
              </div>

              {candidateId && (
                <p className="text-sm text-muted-foreground">
                  Candidate from database
                </p>
              )}
            </div>

            {/* Countdown */}
            <div className="text-sm text-muted-foreground">
              {timeRemaining > 0 ? (
                <span>Auto-reject in {timeRemaining}s</span>
              ) : (
                <span>Rejecting...</span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-6 justify-center pt-4">
              {/* Reject button */}
              <button
                onClick={handleReject}
                className={cn(
                  "flex items-center justify-center",
                  "w-16 h-16 rounded-full",
                  "bg-destructive hover:bg-destructive/90",
                  "text-destructive-foreground",
                  "transition-all duration-200",
                  "hover:scale-110 active:scale-95",
                  "shadow-lg hover:shadow-xl",
                  "focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
                )}
                aria-label="Reject call"
              >
                <PhoneOff className="w-8 h-8" />
              </button>

              {/* Accept button */}
              <button
                onClick={handleAccept}
                className={cn(
                  "flex items-center justify-center",
                  "w-16 h-16 rounded-full",
                  "bg-green-600 hover:bg-green-700",
                  "text-white",
                  "transition-all duration-200",
                  "hover:scale-110 active:scale-95",
                  "shadow-lg hover:shadow-xl",
                  "focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2",
                  "animate-pulse"
                )}
                aria-label="Accept call"
              >
                <Phone className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

